/**
 * PriceDisplay - Componente para exibição formatada de preços
 * 
 * Características:
 * - Formatação consistente de moeda (BRL por padrão)
 * - Suporte a exibição de desconto (preço original riscado)
 * - Tamanhos configuráveis (sm, md, lg)
 * - Opção de mostrar/ocultar símbolo da moeda
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { cn } from '@/lib/utils';

// Props do componente
interface PriceDisplayProps {
  /** Valor a ser exibido */
  amount: number;
  /** Código da moeda (padrão: BRL) */
  currency?: string;
  /** Valor original para mostrar desconto */
  originalAmount?: number;
  /** Tamanho do texto */
  size?: 'sm' | 'md' | 'lg';
  /** Mostrar símbolo da moeda */
  showCurrency?: boolean;
  /** 
   * Ativar truncamento para layouts apertados (ex: Kanban cards)
   * Aplica min-w-0 + truncate nos spans para permitir encolhimento
   * @default false
   */
  truncate?: boolean;
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * Formata um valor numérico como moeda
 */
function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency,
  }).format(value);
}

/**
 * Componente de exibição de preço
 */
export function PriceDisplay({
  amount,
  currency = 'BRL',
  originalAmount,
  size = 'md',
  showCurrency = true,
  truncate = false,
  className,
}: PriceDisplayProps) {
  // Classes de tamanho
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg font-semibold',
  };

  // Formata o valor
  const formattedAmount = showCurrency
    ? formatCurrency(amount, currency)
    : amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 });

  // Formata o valor original (se houver desconto)
  const formattedOriginal = originalAmount
    ? showCurrency
      ? formatCurrency(originalAmount, currency)
      : originalAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
    : null;

  // Verifica se há desconto válido
  const hasDiscount = originalAmount && originalAmount > amount;

  return (
    <div className={cn(
      'flex items-baseline gap-2',
      // v1.1.0: Suporte a truncamento para layouts apertados
      truncate && 'min-w-0 overflow-hidden',
      className
    )}>
      {/* Preço atual */}
      <span
        className={cn(
          sizeClasses[size],
          hasDiscount ? 'text-green-600 dark:text-green-400' : 'text-foreground',
          // v1.1.0: Truncamento quando habilitado
          truncate && 'min-w-0 truncate'
        )}
        title={truncate ? formattedAmount : undefined}
      >
        {formattedAmount}
      </span>

      {/* Preço original (riscado) se houver desconto */}
      {hasDiscount && formattedOriginal && (
        <span 
          className={cn(
            'text-muted-foreground line-through text-sm',
            // v1.1.0: Truncamento quando habilitado
            truncate && 'min-w-0 truncate'
          )}
          title={truncate ? formattedOriginal : undefined}
        >
          {formattedOriginal}
        </span>
      )}
    </div>
  );
}
